import React from 'react';
import {
  StyleSheet,
  Keyboard,
  TouchableWithoutFeedback,
  ScrollView,
  Platform,
} from 'react-native';
import TextInput from '../elements/AVTextInput';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import uuid from 'src/nativeModules/UUID';
import ScreenContext from '../ScreenContext';
import AppRoutes from 'src/AppRoutes';
import Events from 'src/logging/Events';
import SafeAreaView from '../elements/SafeAreaView';
import KeyboardAvoidingView from '../elements/365KeyboardAvoidingView';
import RoundedButton, {ButtonType} from '../elements/RoundedButton';
import Styles from '../Styles';
import ActionsFactory from 'src/actions/ActionsFactory';
import UIManager from '../elements/ui/UIManager';
import AccountStore from 'src/stores/AccountStore';
import PersistentStore from 'src/services/PersistentStoreService';
import SyncHelper from 'src/services/SyncService';
import FocusAwareStatusBar from '../elements/FocusAwareStatusBar';
import type {SnackAction} from 'src/types/Snack';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../helpers/AlertHelper';
import BackSubheader from '../elements/BackSubheader';

type LoginProps = {
  username: string;
  password: string;
  snack?: SnackAction;
};

type LoginState = {
  username: string;
  touches: number;
  password: string;
};

const backgroundColor = Platform.select({
  web: 'white',
  default: Styles.loginBackground,
});

const textColor = Platform.select({
  web: 'black',
  default: Styles.loginText,
});

export const paperInputTheme = {
  colors: {
    text: textColor,
    disabled: textColor,
    placeholder: textColor,
    background: backgroundColor,
  },
};

export class LoginScreen extends React.Component<LoginProps, LoginState> {
  password: TextInput | null;
  static defaultProps = {
    username: '',
    password: '',
  };
  static contextType = ScreenContext;

  constructor(props: LoginProps) {
    super(props);
    this.state = {
      touches: 0,
      username: props.username,
      password: props.password,
    };
    this.fetchUsername = this.fetchUsername.bind(this);
    this.checkIfLoggedIn = this.checkIfLoggedIn.bind(this);
    this._validate = this._validate.bind(this);
    this.displayLoginError = this.displayLoginError.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.doAVLiveLogin = this.doAVLiveLogin.bind(this);
  }

  async componentDidMount() {
    if (this.state.username && this.state.username.length > 0) {
      this.handleClick();
    } else {
      const loggedIn = await this.checkIfLoggedIn();

      if (!loggedIn) {
        this.fetchUsername();
      }
    }
  }

  fetchUsername() {
    try {
      const username = AccountStore.getUsername();

      if (username) {
        if (this.password) {
          this.password.focus();
        }

        this.setState({
          username,
        });
      }
    } catch (error) {
      Events.Error.trackEvent(
        'Exception',
        'LoginScreen:FetchUsername',
        error.message ? error.message : error.toString(),
      );
    }
  }

  async checkIfLoggedIn() {
    const accountId = await PersistentStore.getAccountId();

    if (accountId && accountId !== '-1') {
      const isOperator = AccountStore.getIsOperator();

      if (isOperator) {
        this.context.actions.changeRootRoute(AppRoutes.Operator);
      } else {
        this.context.actions.changeRootRoute(AppRoutes.App);
      }

      return true;
    }

    return false;
  }

  _validate(username, password) {
    if (
      !username ||
      username.length === 0 ||
      !password ||
      password.length === 0
    ) {
      return Localized.Errors.enter_username_password;
    }

    return null;
  }

  displayLoginError() {
    this.context.actions.hideSpinner();
    alertError(Localized.Errors.login_failure);
  }

  async handleClick() {
    const {username, password} = this.state;

    const errorMessage = this._validate(username, password);

    if (errorMessage) {
      alertError(errorMessage);
    } else {
      this.context.actions.showSpinner();
      try {
        this.doAVLiveLogin(username, password);
      } catch (error) {
        Events.Error.trackEvent(
          'Exception',
          'LoginScreen:HandleClick',
          error.message ? error.message : error.toString(),
        );
      } finally {
        this.context.actions.hideSpinner();
      }
    }
  }

  async doAVLiveLogin(username: string, password: string) {
    this.context.actions.showSpinner();

    try {
      const response = await ActionsFactory.getAccountActions().login(
        username,
        password,
      );

      if (response.AccountId && response.AccountId !== '-1') {
        this.context.actions.hideSpinner();

        if (response.IsOperator) {
          this.context.actions.changeRootRoute(AppRoutes.Operator);
          SyncHelper.performSyncWithServer();
        } else if (AccountStore.isDemo()) {
          this.context.actions.changeRootRoute(AppRoutes.App);
        } else {
          // SOS-4814 - don't allow Consumers to login through AV Live.
          this.displayLoginError();
        }
      } else {
        this.displayLoginError();
      }
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      this.context.actions.hideSpinner();
      alertError(Localized.Errors.error_verifying_account, guid);
      Events.Error.trackEvent(
        'Exception',
        'LoginScreen:DoAVLiveLogin',
        error.message ? error.message : error.toString(),
        guid,
      );
    }
  }

  render() {
    return (
      <BackSubheader title={Localized.Labels.sign_in}>
        <TouchableWithoutFeedback
          accessible={false}
          accessibilityLabel="Dismiss Keyboard"
          onPress={() => Keyboard.dismiss()}
        >
          <SafeAreaView style={styles.view}>
            <FocusAwareStatusBar
              barStyle={UIManager.getLoginStatusBarStyle()}
              backgroundColor={Styles.loginBackground}
            />
            <KeyboardAvoidingView style={styles.container} behavior="height">
              <ScrollView
                style={styles.content}
                keyboardDismissMode="interactive"
                automaticallyAdjustContentInsets={false}
                keyboardShouldPersistTaps="handled"
              >
                <TextInput
                  label={Localized.Labels.email}
                  accessibilityLabel="Email"
                  value={this.state.username}
                  autoFocus={false}
                  autoCapitalize="none"
                  autoCorrect={false}
                  onSubmitEditing={() => {
                    if (this.password) {
                      this.password.focus();
                    }
                  }}
                  returnKeyType="next"
                  onChangeText={(event) =>
                    this.setState({
                      username: event || '',
                    })
                  }
                  keyboardType="email-address"
                  textContentType="username"
                  clearButtonMode="while-editing"
                  style={Styles.Style.font500}
                  theme={paperInputTheme}
                />
                <TextInput
                  label={Localized.Labels.password}
                  accessibilityLabel="Password"
                  ref={(password) => {
                    this.password = password;
                  }}
                  returnKeyType="go"
                  onSubmitEditing={() => this.handleClick()}
                  onChangeText={(event) =>
                    this.setState({
                      password: event || '',
                    })
                  }
                  secureTextEntry
                  autoCapitalize="none"
                  textContentType="password"
                  clearButtonMode="always"
                  style={Styles.Style.font500}
                  theme={paperInputTheme}
                />
              </ScrollView>
              <RoundedButton
                buttonType={ButtonType.action}
                accessibilityLabel="Login"
                onPress={this.handleClick}
                text={Localized.Buttons.sign_in}
              />
            </KeyboardAvoidingView>
          </SafeAreaView>
        </TouchableWithoutFeedback>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    backgroundColor,
    flex: 1,
  },
  content: {
    flex: 1,
    marginBottom: Styles.Spacing.m3,
    paddingHorizontal: Styles.Spacing.m3,
  },
  view: {
    backgroundColor,
    flex: 1,
  },
});

export default withForwardedNavigationParams<LoginProps>()(LoginScreen);
